import { useAccountsService } from '@restworld/data-services';
import { useCallback, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { EditEmailModalMode } from './use-edit-email-form';
import { useTranslation } from 'next-i18next';

export interface EmailLoginInput {
  email: string;
}

export type UseVerifyEmailProps = {
  setMode: (mode: EditEmailModalMode) => void;
  setSubmittedEmail: (email: string) => void;
  email?: string;
  methods: UseFormReturn<EmailLoginInput, any>;
};

export function useVerifyEmailForm({ setMode, setSubmittedEmail, email, methods }: UseVerifyEmailProps) {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
    setValue,
    setError,
  } = methods;
  const [loading, setLoading] = useState<boolean>(false);
  const [remainingTimes, setRemainingTimes] = useState<number>(3);

  const acc = useAccountsService();
  const { t } = useTranslation('user-settings');

  const updateEmail = useCallback(
    async (data: EmailLoginInput) => {
      setLoading(true);
      try {
        await acc.updateAuthenticationDetails({ email: data.email });
        setSubmittedEmail(data.email);
        setMode(EditEmailModalMode.EmailSubmitted);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setError('email', {
          message: t('email_verification_failed_on_the_server'),
        });
      }
    },
    [acc, setError, setMode, setSubmittedEmail, t]
  );

  const onSubmit = useCallback(
    async (data: EmailLoginInput) => {
      updateEmail(data);
      setRemainingTimes((prev) => prev - 1);
    },
    [updateEmail]
  );

  const onSetEmail = (email: string) => {
    setValue('email', email);
  };

  return {
    loading,
    onSubmit,
    register,
    handleSubmit,
    errors,
    watch,
    control,
    setValue,
    setError,
    setLoading,
    onSetEmail,
    remainingTimes,
  };
}
